import { useEffect, useState } from 'react'
import { Plus, Pencil, Trash2, Link, Unlink, Key } from 'lucide-react'
import Topbar from '../components/Topbar'
import api from '../api/client'
import useAppStore from '../store/appStore'
import { fmtDate, hoje } from '../utils/fmt'

const emptyForm = { nome: '', nif: '', email: '', telefone: '', morada: '', codigoPostal: '', localidade: '', notas: '', userEmail: '', userPassword: '' }
const emptyAssoc = { fracaoId: '', tipo: 'PROPRIETARIO', dataEntrada: hoje() }

export default function Condominos() {
  const { condominioAtivo } = useAppStore()
  const [condominos, setCondominos] = useState([])
  const [fracoes, setFracoes] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(false)
  const [modalAssoc, setModalAssoc] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [assocForm, setAssocForm] = useState(emptyAssoc)
  const [editId, setEditId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [erro, setErro] = useState('')
  const [sucesso, setSucesso] = useState('')

  const load = async () => {
    if (!condominioAtivo) return
    setLoading(true)
    try {
      const [cRes, fRes] = await Promise.all([
        api.get('/condominos', { params: { condominioId: condominioAtivo.id } }),
        api.get('/fracoes', { params: { condominioId: condominioAtivo.id } })
      ])
      setCondominos(cRes.data)
      setFracoes(fRes.data)
    } finally { setLoading(false) }
  }

  useEffect(() => { load() }, [condominioAtivo])

  const openCreate = () => { setForm(emptyForm); setEditId(null); setErro(''); setSucesso(''); setModal(true) }
  const openEdit = (c) => {
    setForm({
      nome: c.nome, nif: c.nif || '', email: c.email || '', telefone: c.telefone || '',
      morada: c.morada || '', codigoPostal: c.codigoPostal || '', localidade: c.localidade || '',
      notas: c.notas || '', userEmail: c.userEmail || c.user?.email || '', userPassword: c.userPassword || ''
    })
    setEditId(c.id); setErro(''); setSucesso(''); setModal(true)
  }

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true); setErro(''); setSucesso('')
    try {
      if (editId) {
        await api.put(`/condominos/${editId}`, form)
        setSucesso('Guardado com sucesso.')
      } else {
        await api.post('/condominos', form)
        setModal(false)
      }
      load()
    } catch (err) {
      setErro(err.response?.data?.erro || err.response?.data?.erros?.[0]?.msg || 'Erro ao guardar.')
    } finally { setSaving(false) }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Desativar este condómino?')) return
    await api.delete(`/condominos/${id}`)
    load()
  }

  const handleAssociar = async (e) => {
    e.preventDefault(); setSaving(true); setErro('')
    try {
      await api.post('/condominos/associar-fracao', { condominoId: modalAssoc, ...assocForm })
      setModalAssoc(null); load()
    } catch (err) { setErro(err.response?.data?.erro || 'Erro.') }
    finally { setSaving(false) }
  }

  const handleDesassociar = async (condominoId, fracaoId, designacao) => {
    if (!window.confirm(`Remover associação com fração ${designacao}?`)) return
    await api.post('/condominos/desassociar-fracao', { condominoId, fracaoId })
    load()
  }

  const initials = (nome) => nome?.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase() || '?'

  return (
    <>
      <Topbar title="Condóminos" actions={
        <button className="btn btn-primary btn-sm" onClick={openCreate}><Plus size={13} /> Novo condómino</button>
      } />
      <div className="page-content">
        <div className="page-header"><h1>Condóminos</h1><p>{condominos.length} condóminos em {condominioAtivo?.nome}</p></div>
        <div className="card">
          {loading ? <div className="loading-center"><span className="spinner" /></div> : (
            <div className="table-wrap">
              <table className="data-table">
                <thead><tr><th>Nome</th><th>NIF</th><th>Contacto</th><th>Frações</th><th>Acesso</th><th></th></tr></thead>
                <tbody>
                  {condominos.length === 0 ? (
                    <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 32 }}>Sem condóminos.</td></tr>
                  ) : condominos.map(c => (
                    <tr key={c.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--green-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: 'var(--green-dark)', flexShrink: 0 }}>
                            {initials(c.nome)}
                          </div>
                          <div>
                            <div style={{ fontWeight: 500 }}>{c.nome}</div>
                            {c.email && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{c.email}</div>}
                          </div>
                        </div>
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{c.nif || '—'}</td>
                      <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{c.telefone || '—'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                          {c.fracoes?.filter(cf => cf.ativo).map(cf => (
                            <span key={cf.fracaoId} style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                              <span className="badge badge-blue">{cf.fracao.designacao}</span>
                              <button onClick={() => handleDesassociar(c.id, cf.fracaoId, cf.fracao.designacao)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', padding: 0 }} title="Remover">
                                <Unlink size={10} />
                              </button>
                            </span>
                          ))}
                          <button className="btn btn-sm" style={{ padding: '1px 6px', fontSize: 11 }}
                            onClick={() => { setAssocForm(emptyAssoc); setErro(''); setModalAssoc(c.id) }}>
                            <Plus size={10} />
                          </button>
                        </div>
                      </td>
                      <td>
                        {c.user || c.userEmail ? (
                          <div style={{ fontSize: 11 }}>
                            <span className="badge badge-green"><Key size={9} style={{ marginRight: 3 }} />{c.userEmail || c.user?.email}</span>
                          </div>
                        ) : <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Sem acesso</span>}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn btn-sm" onClick={() => openEdit(c)}><Pencil size={12} /></button>
                          <button className="btn btn-sm" onClick={() => handleDelete(c.id)} style={{ color: 'var(--red-dark)' }}><Trash2 size={12} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal" style={{ width: 560 }}>
            <div className="modal-header">
              <span className="modal-title">{editId ? 'Editar condómino' : 'Novo condómino'}</span>
              <button onClick={() => setModal(false)} style={{ background: 'none', border: 'none', fontSize: 20 }}>×</button>
            </div>
            {erro && <div className="alert alert-error">{erro}</div>}
            {sucesso && <div className="alert alert-success">{sucesso}</div>}
            <form onSubmit={handleSave}>
              <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Dados pessoais</div>
              <div className="form-group">
                <label className="form-label">Nome completo *</label>
                <input className="form-input" value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} required />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group"><label className="form-label">NIF</label><input className="form-input" value={form.nif} onChange={e => setForm({ ...form, nif: e.target.value })} /></div>
                <div className="form-group"><label className="form-label">Telefone</label><input className="form-input" value={form.telefone} onChange={e => setForm({ ...form, telefone: e.target.value })} /></div>
              </div>
              <div className="form-group"><label className="form-label">Email pessoal</label><input className="form-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
              <div className="form-group"><label className="form-label">Morada</label><input className="form-input" value={form.morada} onChange={e => setForm({ ...form, morada: e.target.value })} /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
                <div className="form-group"><label className="form-label">Cód. postal</label><input className="form-input" value={form.codigoPostal} onChange={e => setForm({ ...form, codigoPostal: e.target.value })} /></div>
                <div className="form-group"><label className="form-label">Localidade</label><input className="form-input" value={form.localidade} onChange={e => setForm({ ...form, localidade: e.target.value })} /></div>
              </div>
              <div className="form-group"><label className="form-label">Notas</label><textarea className="form-input" rows={2} value={form.notas} onChange={e => setForm({ ...form, notas: e.target.value })} /></div>

              <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--text-secondary)', margin: '16px 0 10px', textTransform: 'uppercase', letterSpacing: '0.05em', paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                Acesso ao portal
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group"><label className="form-label">Email de acesso</label><input className="form-input" type="email" placeholder="fracao@condominio.pt" value={form.userEmail} onChange={e => setForm({ ...form, userEmail: e.target.value })} /></div>
                <div className="form-group"><label className="form-label">Password</label><input className="form-input" type="text" placeholder="lote32" value={form.userPassword} onChange={e => setForm({ ...form, userPassword: e.target.value })} /></div>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: -8, marginBottom: 14 }}>A password é guardada e visível para o administrador poder comunicar ao condómino.</div>

              <div className="modal-footer">
                <button type="button" className="btn" onClick={() => setModal(false)}>Fechar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? <span className="spinner" /> : editId ? 'Guardar' : 'Criar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {modalAssoc && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModalAssoc(null)}>
          <div className="modal">
            <div className="modal-header"><span className="modal-title">Associar fração</span><button onClick={() => setModalAssoc(null)} style={{ background: 'none', border: 'none', fontSize: 20 }}>×</button></div>
            {erro && <div className="alert alert-error">{erro}</div>}
            <form onSubmit={handleAssociar}>
              <div className="form-group"><label className="form-label">Fração *</label>
                <select className="form-select" value={assocForm.fracaoId} onChange={e => setAssocForm({ ...assocForm, fracaoId: e.target.value })} required>
                  <option value="">Selecionar...</option>
                  {fracoes.map(f => <option key={f.id} value={f.id}>{f.designacao} — {f.tipo || ''} {f.valorQuota ? `· ${f.valorQuota}€/mês` : ''}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group"><label className="form-label">Tipo</label>
                  <select className="form-select" value={assocForm.tipo} onChange={e => setAssocForm({ ...assocForm, tipo: e.target.value })}>
                    <option value="PROPRIETARIO">Proprietário</option><option value="INQUILINO">Inquilino</option>
                  </select>
                </div>
                <div className="form-group"><label className="form-label">Data entrada *</label>
                  <input className="form-input" type="date" value={assocForm.dataEntrada} onChange={e => setAssocForm({ ...assocForm, dataEntrada: e.target.value })} required />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn" onClick={() => setModalAssoc(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? <span className="spinner" /> : 'Associar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}
